<template>
  <button
    class="group border border-secondary bg-primary rounded-md p-3 md:py-2 flex gap-2 items-center w-full max-w-full data-[active=true]:bg-brand-primary_alt data-[active=true]:border-brand data-[active=true]:text-brand-secondary"
    :data-active="isSelected"
    @click="updateState"
    :class="{
      'focus-within:ring-brand focus:ring-brand': selectable,
      'opacity-30 cursor-not-allowed': !selectable,
    }"
  >
    <Icon
      v-if="hasChildren"
      name="chevron-down"
      class="transition-transform duration-200 button-tertiary-gray-fg shrink-0"
      :class="{ 'rotate-180': isOpen }"
      @click.stop="emit('toggleChildren')"
    />
    <Indicator
      :variant="indicatorVariant"
      :isGroup="hasChildren"
      :mode="isSelected ? 'solid' : 'transparent'"
      :imageUrl="data.thumbnailUrl"
      size="xs"
    />
    <Component
      :is="!selectable ? 'div' : Tooltip"
      :title="data.name"
      :delay="2000"
      side="bottom"
      align="start"
      class="max-w-full truncate"
      @click.stop="emit('toggleChildren')"
    >
      <span
        class="block ltr:text-left rtl:text-right max-w-full truncate text-base md:text-sm font-medium"
      >
        <span v-for="(part, index) in highlightedParts" :key="index">
          <mark v-if="part.highlight" class="bg-yellow-300">
            {{ part.text }}
          </mark>
          <template v-else>
            {{ part.text }}
          </template>
        </span>
      </span>
    </Component>
    <Id
      v-if="data.identifier"
      :size="maxTablet ? 'md' : 'sm'"
      :label="data.identifier"
      class="group-data-[active=true]:bg-brand-primary group-data-[active=true]:border-transparent group-data-[active=true]:[&>span]:!text-brand-secondary group-data-[active=true]:text-brand-secondary shrink-0 !max-w-24"
    />
    <State
      v-if="showState && multiple"
      :state="state"
      :hasChildren="hasChildren"
      @click.stop="updateState"
    />
  </button>
</template>

<script setup>
import { computed } from "vue";
import { Id } from "@/SharedComponents/Badges";
import { Icon } from "@/SharedComponents/Icon";
import { Tooltip } from "@/SharedComponents/Tooltips";
import { State } from "@/SharedComponents/Filters/Tree";
import { Indicator } from "@/SharedComponents/Indicator";
import { useBreakpoints } from "@/Composables/useBreakpoints";

const { maxTablet } = useBreakpoints();

const props = defineProps({
  isSelected: { type: Boolean, required: false },
  isOpen: { type: Boolean, required: false },
  data: { type: Object, required: true },
  level: { type: Number, default: 1 },
  hasChildren: { type: Boolean, default: false },
  showState: { type: Boolean, required: false },
  searchTerm: { type: String, default: "" },
  state: { type: String, default: "" },
  type: { type: String, required: false },
  multiple: { type: Boolean, default: true },
  selectable: { type: Boolean, default: true },
});

const emit = defineEmits(["updateState", "toggleChildren"]);

const highlightedParts = computed(() => {
  const name = props.data.name || props.data.label;
  if (!props.searchTerm) return [{ text: name, highlight: false }];

  const lowerName = name.toLowerCase();
  const lowerQuery = props.searchTerm.toLowerCase();
  const parts = [];
  let lastIndex = 0;

  lowerName.split(lowerQuery).forEach((part, index, array) => {
    if (part) {
      parts.push({
        text: name.slice(lastIndex, lastIndex + part.length),
        highlight: false,
      });
      lastIndex += part.length;
    }
    if (index < array.length - 1) {
      parts.push({
        text: name.slice(lastIndex, lastIndex + props.searchTerm.length),
        highlight: true,
      });
      lastIndex += props.searchTerm.length;
    }
  });

  return parts;
});

const indicatorVariant = computed(() => {
  if (
    (!props.data.type && props.type === "space" && props.level === 1) ||
    props.data.id === props.data.buildingId
  ) {
    return "building";
  }
  return props.data.type || props.type;
});

const updateState = () => {
  if (props.selectable) {
    emit("updateState");
  }
};
</script>
